import styled, { keyframes } from 'styled-components';

// Animações
const glowPulse = keyframes`
  0% { box-shadow: 0 0 10px rgba(64, 128, 255, 0.4); }
  50% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.7), 0 0 30px rgba(64, 128, 255, 0.5); }
  100% { box-shadow: 0 0 10px rgba(64, 128, 255, 0.4); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const dataFlow = keyframes`
  0% { transform: translateY(0) scale(0.8); opacity: 0; }
  50% { transform: translateY(-15px) scale(1); opacity: 0.8; }
  100% { transform: translateY(-30px) scale(0.8); opacity: 0; }
`;

const scanLine = keyframes`
  0% { transform: translateY(-100%); opacity: 0.5; }
  100% { transform: translateY(100%); opacity: 0; }
`;

const techGridPulse = keyframes`
  0% { opacity: 0.1; }
  50% { opacity: 0.3; }
  100% { opacity: 0.1; }
`;

const digitalCircuitFlow = keyframes`
  0% { background-position: 0% 0%; }
  100% { background-position: 100% 100%; }
`;

const float = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0); }
`;

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.7; }
`;

const energyRing = keyframes`
  0% { transform: scale(0.8); opacity: 0.3; }
  50% { transform: scale(1.2); opacity: 0.7; }
  100% { transform: scale(0.8); opacity: 0.3; }
`;

const fadeInUp = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const progressScan = keyframes`
  0% { width: 0%; opacity: 0.4; }
  50% { width: 50%; opacity: 0.6; }
  100% { width: 100%; opacity: 0.2; }
`;

const subtlePulse = keyframes`
  0% { opacity: 0.7; box-shadow: 0 0 5px rgba(0, 255, 255, 0.3); }
  50% { opacity: 1; box-shadow: 0 0 10px rgba(0, 255, 255, 0.5); }
  100% { opacity: 0.7; box-shadow: 0 0 5px rgba(0, 255, 255, 0.3); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const digitalScan = keyframes`
  0% { 
    transform: translateY(-100%) scaleY(0.3); 
    opacity: 0; 
  }
  20% { 
    transform: translateY(-80%) scaleY(0.5); 
    opacity: 0.7; 
  }
  80% { 
    transform: translateY(80%) scaleY(0.5); 
    opacity: 0.7; 
  }
  100% { 
    transform: translateY(100%) scaleY(0.3); 
    opacity: 0; 
  }
`;

const gridReveal = keyframes`
  0% { 
    opacity: 0;
    transform: scale(0.95);
  }
  20% { 
    opacity: 0.7;
    transform: scale(1);
  }
  80% { 
    opacity: 0.7;
    transform: scale(1);
  }
  100% { 
    opacity: 0;
    transform: scale(0.95);
  }
`;

const dataTravel = keyframes`
  0% { 
    transform: translate(-5px, -5px); 
    opacity: 0; 
  }
  20% { 
    transform: translate(0px, 0px); 
    opacity: 1; 
  }
  80% { 
    transform: translate(0px, 0px); 
    opacity: 1; 
  }
  100% { 
    transform: translate(5px, 5px); 
    opacity: 0; 
  }
`;

// Componentes estilizados
export const ProcessSection = styled.div`
  margin-top: 3rem;
  padding: 2rem;
  position: relative;
  background: rgba(0, 16, 32, 0.7);
  border-radius: 16px;
  border: 1px solid rgba(64, 128, 255, 0.3);
  box-shadow: 0 0 20px rgba(0, 64, 255, 0.2);
  overflow: hidden;
  animation: ${glowPulse} 5s infinite ease-in-out;
  
  /* Efeito de scanner */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background: linear-gradient(to bottom, 
      rgba(0, 255, 255, 0.5), 
      rgba(0, 255, 255, 0)
    );
    animation: ${scanLine} 4s infinite ease-in-out;
    z-index: 1;
  }
  
  /* Background de circuito digital */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 10h10v10H10zM30 10h10v10H30zM50 10h10v10H50zM70 10h10v10H70zM90 10h10v10H90zM10 30h10v10H10zM30 30h10v10H30zM50 30h10v10H50zM70 30h10v10H70zM90 30h10v10H90zM10 50h10v10H10zM30 50h10v10H30zM50 50h10v10H50zM70 50h10v10H70zM90 50h10v10H90zM10 70h10v10H10zM30 70h10v10H30zM50 70h10v10H50zM70 70h10v10H70zM90 70h10v10H90zM10 90h10v10H10zM30 90h10v10H30zM50 90h10v10H50zM70 90h10v10H70zM90 90h10v10H90z' fill='none' stroke='rgba(0, 255, 255, 0.05)' stroke-width='1'/%3E%3Cpath d='M0 20h100M0 40h100M0 60h100M0 80h100M20 0v100M40 0v100M60 0v100M80 0v100' stroke='rgba(0, 255, 255, 0.03)' stroke-width='1'/%3E%3C/svg%3E");
    background-size: 50px 50px;
    opacity: 0.3;
    z-index: -1;
    animation: ${techGridPulse} 4s infinite ease-in-out, ${digitalCircuitFlow} 20s infinite linear;
  }
`;

export const ProcessTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 2rem;
  letter-spacing: 2px;
  color: #FFFFFF;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
  position: relative;
  
  /* Linhas tecnológicas decorativas */
  &::before, &::after {
    content: '';
    position: absolute;
    height: 2px;
    background: linear-gradient(90deg, 
      rgba(0, 32, 64, 0), 
      rgba(0, 255, 255, 0.8), 
      rgba(0, 32, 64, 0)
    );
    width: 80%;
    left: 10%;
  }
  
  &::before {
    top: -10px;
  }
  
  &::after {
    bottom: -10px;
  }
`;

export const ProcessFlow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  margin-top: 2rem;
  
  /* Linha de fluxo vertical removida */
  
  @media (max-width: 768px) {
    /* Ajuste para responsividade */
  }
`;

export const StepContainer = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 3rem;
  position: relative;
  z-index: 1;
  opacity: 0;
  animation: ${fadeInUp} 0.8s forwards;
  animation-delay: ${props => `${props.index * 0.2}s`};
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding-left: 60px;
  }
`;

export const StepIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: rgba(0, 32, 64, 0.6);
  border: 2px solid rgba(0, 255, 255, 0.5);
  position: relative;
  z-index: 2;
  margin: 0 2rem;
  animation: ${pulse} 4s infinite ease-in-out;
  flex-shrink: 0;
  
  /* Gradiente de fundo */
  background: linear-gradient(135deg, 
    rgba(0, 32, 64, 0.8) 0%, 
    rgba(0, 64, 128, 0.7) 50%, 
    rgba(0, 32, 64, 0.8) 100%
  );
  background-size: 200% 200%;
  animation: ${gradientMove} 10s ease infinite, ${pulse} 4s infinite ease-in-out;
  
  /* Círculos concêntricos */
  &::before, &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(0, 255, 255, 0.3);
    animation: ${energyRing} 4s infinite ease-in-out;
  }
  
  &::before {
    width: 100%;
    height: 100%;
    animation-delay: 0.5s;
  }
  
  &::after {
    width: 120%;
    height: 120%;
    animation-delay: 1s;
  }
  
  @media (max-width: 768px) {
    position: absolute;
    left: 0;
    margin: 0;
    transform: translateX(-50%);
    width: 60px;
    height: 60px;
  }
`;

export const StepIcon = styled.div`
  color: #00FFFF;
  font-size: 2rem;
  filter: drop-shadow(0 0 8px rgba(0, 255, 255, 0.7));
  
  /* Elemento rotativo interno */
  &::after {
    content: '';
    position: absolute;
    width: 30px;
    height: 30px;
    border: 2px solid transparent;
    border-top: 2px solid rgba(0, 255, 255, 0.5);
    border-left: 2px solid rgba(0, 255, 255, 0.5);
    border-radius: 50%;
    animation: ${rotate} 8s linear infinite;
  }
`;

export const StepContent = styled.div`
  flex: 1;
  background: rgba(0, 32, 64, 0.4);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(64, 128, 255, 0.2);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: ${subtlePulse} 6s infinite ease-in-out;
  animation-delay: ${props => `${props.index * 0.5}s`};
  
  /* Efeito de escaneamento digital */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to bottom,
      rgba(0, 255, 255, 0.01) 0%,
      rgba(0, 255, 255, 0.08) 20%,
      rgba(0, 255, 255, 0.1) 50%,
      rgba(0, 255, 255, 0.08) 80%,
      rgba(0, 255, 255, 0.01) 100%
    );
    pointer-events: none;
    z-index: 1;
    opacity: 0;
    animation: ${digitalScan} 5s infinite ease-in-out;
    animation-delay: ${props => `${props.index * 0.7}s`};
  }
  
  /* Grade tecnológica que aparece e desaparece */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      linear-gradient(to right, rgba(0, 255, 255, 0.05) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(0, 255, 255, 0.05) 1px, transparent 1px);
    background-size: 20px 20px;
    pointer-events: none;
    z-index: 0;
    opacity: 0;
    animation: ${gridReveal} 8s infinite ease-in-out;
    animation-delay: ${props => `${props.index * 0.5}s`};
  }
  
  /* Hover effect */
  &:hover {
    box-shadow: 0 0 15px rgba(0, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

export const StepCorner = styled.div`
  position: absolute;
  width: 15px;
  height: 15px;
  z-index: 2;
  
  &.top-left {
    top: 0;
    left: 0;
    border-top: 1px solid rgba(0, 255, 255, 0.7);
    border-left: 1px solid rgba(0, 255, 255, 0.7);
  }
  
  &.top-right {
    top: 0;
    right: 0;
    border-top: 1px solid rgba(0, 255, 255, 0.7);
    border-right: 1px solid rgba(0, 255, 255, 0.7);
  }
  
  &.bottom-left {
    bottom: 0;
    left: 0;
    border-bottom: 1px solid rgba(0, 255, 255, 0.7);
    border-left: 1px solid rgba(0, 255, 255, 0.7);
  }
  
  &.bottom-right {
    bottom: 0;
    right: 0;
    border-bottom: 1px solid rgba(0, 255, 255, 0.7);
    border-right: 1px solid rgba(0, 255, 255, 0.7);
  }
`;

export const DataPoint = styled.div`
  position: absolute;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background-color: rgba(0, 255, 255, 0.8);
  box-shadow: 0 0 5px rgba(0, 255, 255, 0.8), 0 0 10px rgba(0, 255, 255, 0.4);
  z-index: 2;
  animation: ${dataTravel} 4s infinite ease-in-out;
  
  &.top-left {
    top: 5px;
    left: 5px;
    animation-delay: ${props => `${props.index * 0.3}s`};
  }
  
  &.top-right {
    top: 5px;
    right: 5px;
    animation-delay: ${props => `${props.index * 0.5}s`};
  }
  
  &.bottom-left {
    bottom: 5px;
    left: 5px;
    animation-delay: ${props => `${props.index * 0.7}s`};
  }
  
  &.bottom-right {
    bottom: 5px;
    right: 5px;
    animation-delay: ${props => `${props.index * 0.9}s`};
  }
`;

export const StepNumber = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(0, 32, 64, 0.9);
  border: 1px solid rgba(0, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.7rem;
  font-weight: bold;
  color: #00FFFF;
  z-index: 3;
  box-shadow: 0 0 8px rgba(0, 255, 255, 0.5);
`;

export const StepTitle = styled.h4`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.2rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #FFFFFF;
  letter-spacing: 1px;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
  
  /* Gradiente animado */
  background: linear-gradient(90deg, #64B6FF, #00FFFF, #64B6FF);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  background-size: 200% auto;
  animation: ${gradientMove} 5s linear infinite;
`;

export const StepDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
`;

export const DataParticle = styled.div`
  position: absolute;
  width: 4px;
  height: 4px;
  background-color: ${props => props.color || '#00FFFF'};
  border-radius: 50%;
  top: ${props => props.top};
  left: ${props => props.left};
  opacity: 0;
  animation: ${dataFlow} ${props => props.duration || '2s'} infinite;
  animation-delay: ${props => props.delay || '0s'};
  z-index: 1;
  box-shadow: 0 0 5px ${props => props.color || '#00FFFF'};
`;

export const TechCircle = styled.div`
  position: absolute;
  width: ${props => props.size || '20px'};
  height: ${props => props.size || '20px'};
  border-radius: 50%;
  border: 1px solid rgba(0, 255, 255, ${props => props.opacity || '0.3'});
  top: ${props => props.top};
  left: ${props => props.left};
  animation: ${pulse} ${props => props.duration || '4s'} infinite ease-in-out;
  animation-delay: ${props => props.delay || '0s'};
  z-index: 0;
`;
